import React, { useContext, useMemo, useRef, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, useTheme
} from '@mui/material'
import { PatternFormat } from 'react-number-format'
import Text from '../Text'
import TextFieldCustom from '../TextFieldCustom'
import TestSendMessageModal from '../TestSendMessageModal'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import { isMetaOfficialProvider } from '../../libs/metaTemplates'
import Pilula from '../shared/disparo/Pilula'
import SeletorDeModelo from '../shared/disparo/SeletorDeModelo'
import ListaDeDestinatarios from '../shared/disparo/ListaDeDestinatarios'
import ResumoDaConferencia from '../shared/disparo/ResumoDaConferencia'
import useConferenciaDoDisparo from '../MessageSender/useConferenciaDoDisparo'
import { numero } from '../../libs/disparos'
import {
  DIAS_SEMANA, FUSO_PADRAO, montarStartAt, descreverRecorrencia,
  aplicarVariaveis, montarSnippetTemplate, variaveisDoTemplate
} from '../../libs/scheduledMessages'

const TIPOS = [
  { valor: 'once', rotulo: 'Uma vez' },
  { valor: 'daily', rotulo: 'Todo dia' },
  { valor: 'weekly', rotulo: 'Dias da semana' },
  { valor: 'monthly', rotulo: 'Todo mês' },
  { valor: 'everyNDays', rotulo: 'A cada N dias' }
]

const VARIAVEIS_FIXAS = ['nome', 'telefone']

const REGEX_HORA = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/

function hojeISO() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO_PADRAO, year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(new Date())
}

function Secao({ numero, titulo, dica, children }) {
  const theme = useTheme()
  return (
    <div
      className="p-4 rounded-xl mb-4"
      style={{ border: `1px solid ${theme.palette.divider}` }}
    >
      <Text
        className="text-xs font-bold uppercase tracking-wide"
        style={{ color: theme.palette.primary.main }}
      >
        {numero} · {titulo}
      </Text>
      {dica && <Text className="font-light text-sm mb-3">{dica}</Text>}
      <div className="mt-2">{children}</div>
    </div>
  )
}

export default function Editor({ aberto, agendamento = {}, salvando, onFechar, onSalvar }) {
  const theme = useTheme()
  const editando = !!agendamento._id
  const refMensagem = useRef(null)

  const [titulo, setTitulo] = useState(agendamento.title || '')
  const [mensagem, setMensagem] = useState(agendamento?.content?.message || '')
  const [anexoUrl, setAnexoUrl] = useState(agendamento?.content?.file?.url || '')

  // Colunas da planilha importada: viram variáveis.
  const [colunasCsv, setColunasCsv] = useState([])

  // A lista é o ESTADO da tela, não algo derivado do CSV ou do textarea. Antes
  // era derivada, e por isso os destinatários já salvos ficavam invisíveis na
  // edição: dava pra ver a contagem, mas não quem eram, nem remover um errado.
  const [lista, setLista] = useState(agendamento?.audience?.numbers || [])

  const s = agendamento.schedule || {}
  const [tipo, setTipo] = useState(s.kind || 'daily')
  const [hora, setHora] = useState(s.time || '09:00')
  const [inicio, setInicio] = useState(hojeISO())
  const [fim, setFim] = useState('')
  const [diasSemana, setDiasSemana] = useState(s.weekDays || [1, 3, 5])
  const [diaDoMes, setDiaDoMes] = useState(s.monthDay || 1)
  const [intervaloDias, setIntervaloDias] = useState(s.intervalDays || 15)

  const [erro, setErro] = useState('')
  const [testeAberto, setTesteAberto] = useState(false)

  // ── Conexão Meta Oficial ───────────────────────────────────────────────────
  // Em número Oficial a mensagem SÓ sai por template aprovado. Deixar o campo
  // de texto livre aqui seria pior que não ter a funcionalidade: o agendamento
  // salvaria com sucesso e falharia calado, todo dia, na hora de enviar.
  const { getCredentials, provider } = useContext(ApiDataWrapperContext)
  const oficial = isMetaOfficialProvider(provider)
  // Lido no render, e não dentro do efeito: quando o editor abre antes de a
  // credencial estar resolvida, o efeito precisa rodar de novo assim que ela
  // chegar. Dependendo só de `oficial`, ele desistia na primeira passada e a
  // lista ficava vazia para sempre.
  const { api: apiCred, secret: secretCred } = getCredentials()

  // Modelo escolhido e o que entra em cada campo (na ordem dos campos). O
  // carregamento dos aprovados mora no SeletorDeModelo, compartilhado com o
  // disparo em massa.
  const [templateNome, setTemplateNome] = useState('')
  const [valoresVars, setValoresVars] = useState([])
  const [templateAtual, setTemplateAtual] = useState(null)

  const aoMudarModelo = ({ nome, valores, template }) => {
    setTemplateNome(nome)
    setValoresVars(valores)
    setTemplateAtual(template)
  }

  const listaFinal = lista

  // As variáveis disponíveis saem das colunas do CSV e também de qualquer
  // `vars` que já esteja na lista (caso de agendamento salvo sendo editado).
  const variaveis = useMemo(() => {
    const doCsv = colunasCsv
    const daLista = new Set()
    lista.forEach((d) => Object.keys(d.vars || {}).forEach((k) => daLista.add(k)))
    return [...new Set([...VARIAVEIS_FIXAS, ...doCsv, ...daLista])]
  }, [colunasCsv, lista])

  const schedulePreview = {
    kind: tipo, time: hora, weekDays: diasSemana, monthDay: diaDoMes, intervalDays: intervaloDias
  }

  const inserirVariavel = (nome) => {
    setMensagem((atual) => `${atual}{{${nome}}}`)
    if (refMensagem.current) refMensagem.current.focus()
  }

  // Em conexão Oficial o que vai pro backend é o SNIPPET do template, não texto
  // livre: é assim que o secondary descobre qual modelo aprovado usar.
  const mensagemFinal = oficial
    ? montarSnippetTemplate(templateAtual, valoresVars)
    : mensagem

  // Custo de UMA ocorrência pela mesma conferência do disparo em massa. Só
  // informa: o agendamento não é barrado aqui, cada mensagem é conferida na
  // hora do envio. O master quer o valor de {{n}} em valores[n - 1]; o editor
  // guarda na ordem dos campos — daí a conversão.
  const conteudoDaConferencia = useMemo(() => {
    if (oficial) {
      if (!templateNome || !templateAtual) return null
      const valores = []
      variaveisDoTemplate(templateAtual).forEach((n, i) => { valores[n - 1] = valoresVars[i] })
      return { tipo: 'modelo', nome: templateNome, valores }
    }
    if (!mensagem.trim()) return null
    return anexoUrl.trim() ? { tipo: 'texto', message: mensagem, fileUrl: anexoUrl.trim() } : { tipo: 'texto', message: mensagem }
  }, [oficial, templateNome, templateAtual, valoresVars, mensagem, anexoUrl])
  const conferir = aberto && !!conteudoDaConferencia && listaFinal.length > 0
  const custoDaOcorrencia = useConferenciaDoDisparo({
    api: apiCred,
    secret: secretCred,
    destinatarios: conferir ? listaFinal.map((d) => ({ phone: d.phone, name: d.name, vars: d.vars })) : [],
    conteudo: conferir ? conteudoDaConferencia : null
  })

  const submeter = () => {
    if (!titulo.trim()) return setErro('Dê um nome ao agendamento')
    if (oficial && !templateAtual) return setErro('Escolha o modelo aprovado que será enviado')
    if (!oficial && !mensagem.trim()) return setErro('Escreva a mensagem que será enviada')
    if (!REGEX_HORA.test(hora)) return setErro('Horário inválido — use o formato HH:mm')
    if (!listaFinal.length) return setErro('Informe ao menos um destinatário')
    if (tipo === 'weekly' && !diasSemana.length) return setErro('Escolha ao menos um dia da semana')
    if (fim && fim <= inicio) return setErro('A data de término precisa ser depois do início')

    setErro('')
    onSalvar({
      ...(editando ? { _id: agendamento._id } : {}),
      title: titulo.trim(),
      content: {
        message: mensagemFinal,
        ...(anexoUrl.trim() ? { file: { url: anexoUrl.trim() } } : {})
      },
      audience: { mode: 'list', numbers: listaFinal },
      schedule: {
        kind: tipo,
        time: hora,
        timezone: FUSO_PADRAO,
        // montarStartAt manda MEIO-DIA local: o master lê o DIA deste campo no
        // fuso do cliente, e meia-noite UTC cairia no dia anterior em Brasília.
        startAt: montarStartAt(inicio, FUSO_PADRAO),
        endAt: fim ? montarStartAt(fim, FUSO_PADRAO) : null,
        ...(tipo === 'weekly' ? { weekDays: diasSemana } : {}),
        ...(tipo === 'monthly' ? { monthDay: Number(diaDoMes) } : {}),
        ...(tipo === 'everyNDays' ? { intervalDays: Number(intervaloDias) } : {})
      }
    })
  }

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>
        <Text component="span" className="font-bold text-lg">
          {editando ? 'Editar agendamento' : 'Novo agendamento'}
        </Text>
      </DialogTitle>

      <DialogContent dividers>
        <Secao numero="1" titulo="O que enviar" dica="Use as variáveis para personalizar cada mensagem.">
          <div className="mb-3">
            <TextFieldCustom
              label="Nome do agendamento"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Aviso de vencimento — 3 dias antes"
            />
          </div>
          {oficial ? (
            /* Conexão Oficial: sem texto livre. A Meta só entrega template
               aprovado quando a conversa parte de nós, e é o que o agendamento
               sempre faz. */
            <div>
              <div
                className="p-3 rounded-lg mb-3"
                style={{
                  border: `1px solid ${theme.palette.primary.main}44`,
                  backgroundColor: `${theme.palette.primary.main}14`
                }}
              >
                <Text className="text-sm">
                  Esta conexão é <b>Meta Oficial</b>. O envio precisa usar um
                  <b> modelo aprovado</b> — escolha abaixo e preencha os campos dele.
                </Text>
              </div>

              <SeletorDeModelo
                api={apiCred}
                secret={secretCred}
                nome={templateNome}
                valores={valoresVars}
                variaveis={variaveis}
                snippetSalvo={agendamento?.content?.message}
                onChange={aoMudarModelo}
                onErro={setErro}
              />
            </div>
          ) : (
            <>
              <TextFieldCustom
                label="Mensagem"
                multiline
                rows={5}
                value={mensagem}
                inputRef={refMensagem}
                onChange={(e) => setMensagem(e.target.value)}
                placeholder="Olá {{nome}}, sua fatura vence em {{vencimento}}."
              />
              <div className="flex items-center flex-wrap mt-2">
                <Text component="span" className="text-xs mr-2 opacity-70">Clique para inserir:</Text>
                {variaveis.map((nome) => (
                  <button
                    key={nome}
                    type="button"
                    onClick={() => inserirVariavel(nome)}
                    className="text-xs px-2 py-1 rounded mr-2 mb-1 font-mono"
                    style={{
                      cursor: 'pointer',
                      border: `1px dashed ${theme.palette.divider}`,
                      color: theme.palette.primary.main,
                      backgroundColor: `${theme.palette.primary.main}14`
                    }}
                  >
                    {`{{${nome}}}`}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="mt-3">
            <TextFieldCustom
              label="Anexo (opcional)"
              value={anexoUrl}
              onChange={(e) => setAnexoUrl(e.target.value)}
              placeholder="https://..."
              helperText="Endereço https público do arquivo."
            />
          </div>
        </Secao>

        <Secao numero="2" titulo="Para quem">
          <ListaDeDestinatarios
            lista={lista}
            onChange={setLista}
            onColunas={setColunasCsv}
            onErro={setErro}
          />

          {variaveis.length > VARIAVEIS_FIXAS.length && (
            <Text className="font-light text-xs mt-2">
              Variáveis disponíveis: {variaveis.map((v) => `{{${v}}}`).join(', ')}
            </Text>
          )}
        </Secao>

        <Secao numero="3" titulo="Quando repetir">
          <div className="mb-3">
            {TIPOS.map((t) => (
              <Pilula key={t.valor} ativo={tipo === t.valor} onClick={() => setTipo(t.valor)}>
                {t.rotulo}
              </Pilula>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <div style={{ width: 130 }}>
              <PatternFormat
                customInput={TextFieldCustom}
                label="Horário"
                format="##:##"
                value={hora}
                valueIsNumericString={false}
                onChange={(e) => setHora(e.target.value)}
              />
            </div>
            <div style={{ width: 170 }}>
              <TextFieldCustom
                label="Começa em"
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div style={{ width: 170 }}>
              <TextFieldCustom
                label="Termina em"
                type="date"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Opcional"
              />
            </div>
            {tipo === 'monthly' && (
              <div style={{ width: 130 }}>
                <TextFieldCustom
                  label="Dia do mês"
                  type="number"
                  value={diaDoMes}
                  onChange={(e) => setDiaDoMes(e.target.value)}
                />
              </div>
            )}
            {tipo === 'everyNDays' && (
              <div style={{ width: 130 }}>
                <TextFieldCustom
                  label="A cada (dias)"
                  type="number"
                  value={intervaloDias}
                  onChange={(e) => setIntervaloDias(e.target.value)}
                />
              </div>
            )}
          </div>

          {tipo === 'weekly' && (
            <div className="mt-3">
              <Text className="text-xs opacity-70 mb-1">Dias da semana</Text>
              <div>
                {DIAS_SEMANA.map((dia) => (
                  <Pilula
                    key={dia.valor}
                    ativo={diasSemana.includes(dia.valor)}
                    onClick={() => setDiasSemana((atual) => (
                      atual.includes(dia.valor)
                        ? atual.filter((d) => d !== dia.valor)
                        : [...atual, dia.valor]
                    ))}
                  >
                    {dia.curto}
                  </Pilula>
                ))}
              </div>
            </div>
          )}

          <div
            className="mt-3 p-3 rounded-lg"
            style={{
              border: `1px solid ${theme.palette.primary.main}44`,
              backgroundColor: `${theme.palette.primary.main}14`
            }}
          >
            <Text className="text-sm">
              {descreverRecorrencia(schedulePreview)}, no horário de Brasília.
            </Text>
            <Text className="font-light text-xs mt-1">
              Fora do expediente de envios nada sai: o agendamento respeita a janela
              configurada, o ritmo do seu plano e o limite diário de segurança do número.
            </Text>
          </div>
        </Secao>

        {conferir && (
          <div className="mb-4">
            <ResumoDaConferencia
              informativo
              conferencia={custoDaOcorrencia.conferencia}
              carregando={custoDaOcorrencia.carregando || !custoDaOcorrencia.conferencia}
              erro={custoDaOcorrencia.erro}
              para={`${numero(listaFinal.length)} ${listaFinal.length === 1 ? 'destinatário' : 'destinatários'}`}
            />
          </div>
        )}

        {erro && (
          <div className="p-3 rounded-lg" style={{ backgroundColor: '#ef535022' }}>
            <Text className="text-sm font-medium" style={{ color: '#ef5350' }}>{erro}</Text>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setTesteAberto(true)}
          disabled={!mensagemFinal.trim()}
          sx={{ textTransform: 'none' }}
        >
          Enviar teste
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={onFechar} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button
          variant="contained"
          disableElevation
          onClick={submeter}
          disabled={salvando}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}
        >
          {salvando ? 'Salvando…' : (editando ? 'Salvar alterações' : 'Criar agendamento')}
        </Button>
      </DialogActions>

      {/* Reusa o modal da Documentação: ele já tem seletor de país, validação e
          trata o "QR CODE NÃO ESCANEADO". O texto vai com as variáveis já
          resolvidas pelo primeiro destinatário da lista — mandar "{{nome}}" cru
          no teste faria o cliente achar que a variável não funciona. */}
      <TestSendMessageModal
        open={testeAberto}
        onClose={() => setTesteAberto(false)}
        title="Enviar teste deste agendamento"
        template={aplicarVariaveis(mensagemFinal, listaFinal[0])}
      />
    </Dialog>
  )
}
